import { useMemo, useState } from 'react'
import {
  Alert, Badge, Box, Button, Group, Loader, Modal, NumberInput, Select, Stack, Switch,
  Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

interface FlowRow {
  srcaddr: string | null
  dstaddr: string | null
  dstport: number | null
  protocol: string | null
  action: string | null
  packets: number | null
  bytes: number | null
}

interface FlowResp {
  status: 'cwl' | 's3' | 'none'
  rows: FlowRow[]
}

interface FlowStatus {
  status: 'cwl' | 's3' | 'none'
  log_group_name?: string
  resource_id?: string
}

export function FlowLogsCard({
  tenantId, region, vpcIds, peerCidr,
}: {
  tenantId: number
  region: string
  vpcIds: string[]
  peerCidr: string
}) {
  const qc = useQueryClient()
  const [actionFilter, setActionFilter] = useState<string | null>(null)
  const [enableOpen, setEnableOpen] = useState(false)
  const [vpcId, setVpcId] = useState<string | null>(null)
  const [enabling, setEnabling] = useState(false)
  // vpcIds vazio = sem filtro de resource_id (status lista todos os Flow Logs da conta)
  const resourceIds = vpcIds.join(',')

  const { data: vpcData } = useQuery<{ vpcs: { vpc_id: string; cidr: string; name: string }[] }, Error>({
    queryKey: ['vpn-diag-vpcs', tenantId, region],
    queryFn: async () => (await api.get(
      `/v1/tenants/${tenantId}/inventory/vpn/diagnostics/vpcs`, { params: { region } })).data,
    enabled: enableOpen && !!tenantId && !!region, retry: false,
  })
  const vpcOptions = (vpcData?.vpcs ?? []).map((v) => ({
    value: v.vpc_id, label: v.name ? `${v.name} — ${v.vpc_id} (${v.cidr})` : `${v.vpc_id} (${v.cidr})`,
  }))

  const { data: status } = useQuery<FlowStatus, Error>({
    queryKey: ['vpn-flowlogs-status', tenantId, region, resourceIds],
    queryFn: async () => (await api.get(
      `/v1/tenants/${tenantId}/inventory/vpn/diagnostics/flowlogs/status`,
      { params: { region, resource_ids: resourceIds } },
    )).data,
    enabled: !!tenantId && !!region,
    retry: false,
  })

  const now = useMemo(() => Math.floor(Date.now() / 1000), [])
  const { data, isLoading, refetch, isFetching } = useQuery<FlowResp, Error>({
    queryKey: ['vpn-flowlogs', tenantId, region, resourceIds, actionFilter],
    queryFn: async () => (await api.get(
      `/v1/tenants/${tenantId}/inventory/vpn/diagnostics/flowlogs`,
      {
        params: {
          region,
          resource_ids: resourceIds,
          start_epoch: now - 3 * 3600,
          end_epoch: now,
          peer_cidr: peerCidr,
          action: actionFilter ?? '',
        },
      },
    )).data,
    enabled: false,
    retry: false,
  })

  async function submitEnable() {
    if (!vpcId) { notifications.show({ title: 'Erro', message: 'Selecione uma VPC.', color: 'red' }); return }
    setEnabling(true)
    try {
      await api.post(`/v1/tenants/${tenantId}/inventory/vpn/diagnostics/flowlogs/enable`,
        { region, vpc_id: vpcId })
      notifications.show({ title: 'Flow Logs habilitados', message: 'Provisionamento concluído.', color: 'teal' })
      setEnableOpen(false)
      qc.invalidateQueries({ queryKey: ['vpn-flowlogs-status', tenantId, region, resourceIds] })
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      notifications.show({ title: 'Erro', message: String(err?.response?.data?.detail ?? 'Falha ao habilitar Flow Logs'), color: 'red' })
    } finally { setEnabling(false) }
  }

  return (
    <Box mb="lg" data-testid="flow-logs-card">
      <Title order={5} mb="xs">Flow Logs</Title>
      {!status ? (
        <Loader size="sm" />
      ) : status.status === 'none' ? (
        <Group>
          <Text size="sm" c="dimmed">Flow Logs não configurados nesta conta.</Text>
          <Button size="xs" variant="light" onClick={() => setEnableOpen(true)}>
            Habilitar Flow Logs
          </Button>
        </Group>
      ) : (
        <Stack gap="xs">
          <Group>
            <Select
              size="xs"
              placeholder="Ação"
              clearable
              value={actionFilter}
              onChange={setActionFilter}
              data={[
                { value: 'REJECT', label: 'REJECT' },
                { value: 'ACCEPT', label: 'ACCEPT' },
              ]}
            />
            <Button size="xs" onClick={() => refetch()} loading={isFetching}>
              Consultar (3h)
            </Button>
            <Text size="xs" c="dimmed">
              {status?.status === 'cwl' ? 'CloudWatch Logs' : status?.status === 's3' ? 'S3/Athena' : ''}
            </Text>
          </Group>
          {isLoading && <Loader size="sm" />}
          {data && (
            <Table striped withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Origem</Table.Th>
                  <Table.Th>Destino</Table.Th>
                  <Table.Th>Porta</Table.Th>
                  <Table.Th>Proto</Table.Th>
                  <Table.Th>Ação</Table.Th>
                  <Table.Th>Pacotes</Table.Th>
                  <Table.Th>Bytes</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.rows.map((r, i) => (
                  <Table.Tr
                    key={i}
                    style={r.action === 'REJECT' ? { background: 'var(--mantine-color-red-light)' } : undefined}
                  >
                    <Table.Td><Text size="xs" ff="monospace">{r.srcaddr ?? '—'}</Text></Table.Td>
                    <Table.Td><Text size="xs" ff="monospace">{r.dstaddr ?? '—'}</Text></Table.Td>
                    <Table.Td>{r.dstport ?? '—'}</Table.Td>
                    <Table.Td>{r.protocol ?? '—'}</Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={r.action === 'REJECT' ? 'red' : 'green'}>
                        {r.action}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{r.packets ?? '—'}</Table.Td>
                    <Table.Td>{r.bytes ?? '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      )}
      <Modal opened={enableOpen} onClose={() => setEnableOpen(false)} title="Habilitar Flow Logs">
        <Stack>
          <Text size="sm" c="dimmed">
            Isto cria um Log Group e uma IAM role de entrega na conta do tenant
            (observabilidade de baixo risco — sem impacto no tráfego). A ação é auditada.
          </Text>
          <Select
            label="VPC alvo" placeholder="Selecione a VPC" searchable
            data={vpcOptions} value={vpcId} onChange={setVpcId}
            data-testid="flowlogs-vpc-select"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEnableOpen(false)}>Cancelar</Button>
            <Button onClick={submitEnable} loading={enabling} disabled={!vpcId}>Confirmar</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}

// ── Interfaces internas do CaptureCard ──────────────────────────────────────

interface Ec2InstanceItem {
  instance_id: string
  name?: string
}

interface Ec2ListResponse {
  // A API /inventory/ec2 retorna { instances: [...] } (mesmo shape de Recursos.tsx)
  instances: Ec2InstanceItem[]
}

interface CaptureStartResponse {
  job_id: number
  status: 'running'
}

interface CaptureStatusResponse {
  status: 'running' | 'ready' | 'failed'
  download_url?: string
  error?: string
}

export function CaptureCard({
  tenantId, region, peerCidr, hasPci,
}: {
  tenantId: number
  region: string
  peerCidr: string
  hasPci: boolean
}) {
  const [instanceId, setInstanceId] = useState<string | null>(null)
  const [cidr, setCidr] = useState(peerCidr)
  const [duration, setDuration] = useState<number | string>(30)
  const [fullPayload, setFullPayload] = useState(false)
  const [jobId, setJobId] = useState<number | null>(null)
  const [captureRegion, setCaptureRegion] = useState(region)
  const [starting, setStarting] = useState(false)
  const [captureError, setCaptureError] = useState<string | null>(null)

  // Busca instâncias EC2 do tenant
  const { data: ec2Data, isLoading: ec2Loading, isError: ec2Error } = useQuery<Ec2ListResponse, Error>({
    queryKey: ['ec2-instances', tenantId],
    queryFn: async () => (await api.get(`/v1/tenants/${tenantId}/inventory/ec2`)).data,
    enabled: !!tenantId,
    retry: false,
  })

  const instanceOptions = (ec2Data?.instances ?? []).map((i) => ({
    value: i.instance_id,
    label: i.name ? `${i.name} (${i.instance_id})` : i.instance_id,
  }))
  const noInstances = !ec2Loading && !ec2Error && instanceOptions.length === 0

  // Polling de status da captura
  const { data: captureStatus } = useQuery<CaptureStatusResponse, Error>({
    queryKey: ['vpn-capture-status', tenantId, jobId],
    queryFn: async () => (await api.get(
      `/v1/tenants/${tenantId}/inventory/vpn/diagnostics/capture/${jobId}`,
      { params: { region: captureRegion } },
    )).data,
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data
      return data?.status === 'running' ? 3000 : false
    },
    retry: false,
  })

  async function startCapture() {
    if (!instanceId || !region) {
      setCaptureError('Selecione uma instância para capturar.')
      return
    }
    setStarting(true)
    setCaptureError(null)
    try {
      const res = await api.post<CaptureStartResponse>(
        `/v1/tenants/${tenantId}/inventory/vpn/diagnostics/capture`,
        {
          region,
          instance_id: instanceId,
          peer_cidr: cidr,
          duration_s: Number(duration),
          full_payload: fullPayload,
        },
      )
      setJobId(res.data.job_id)
      setCaptureRegion(region)
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } }
      const status = err?.response?.status
      const detail = String(err?.response?.data?.detail ?? '')
      let msg: string
      if (status === 409) msg = 'Já existe uma captura em andamento nesta instância.'
      else if (detail.includes('full_payload')) msg = 'Captura de payload completo não permitida neste tenant.'
      else if (detail.includes('region_instance_required')) msg = 'Selecione uma instância e a região.'
      else if (detail.includes('invalid_peer_cidr')) msg = 'CIDR do peer inválido.'
      else msg = detail || 'Erro ao iniciar captura.'
      setCaptureError(msg)
      notifications.show({ title: 'Erro na captura', message: msg, color: 'red' })
    } finally {
      setStarting(false)
    }
  }

  const isRunning = captureStatus?.status === 'running'
  const isReady = captureStatus?.status === 'ready'
  const isFailed = captureStatus?.status === 'failed'

  return (
    <Box mb="lg">
      <Title order={5} mb="xs">Captura de pacotes</Title>
      <Text size="xs" c="dimmed" mb="sm">
        Captura tráfego de rede na instância EC2 via tcpdump (somente cabeçalhos por padrão).
        A captura de payload completo requer tenant sem conformidade PCI.
      </Text>
      <Stack gap="sm">
        {ec2Loading && <Text size="xs" c="dimmed">Carregando instâncias…</Text>}
        {ec2Error && <Text size="xs" c="red">Falha ao carregar instâncias.</Text>}
        {noInstances && <Text size="xs" c="dimmed">Nenhuma instância EC2 disponível neste tenant.</Text>}
        <Select
          label="Instância EC2"
          placeholder={ec2Loading ? 'Carregando...' : 'Selecione uma instância'}
          data={instanceOptions}
          value={instanceId}
          onChange={setInstanceId}
          disabled={ec2Loading || !!jobId}
          searchable
          required
        />
        <TextInput
          label="CIDR do peer"
          placeholder="ex: 172.16.0.0/16"
          value={cidr}
          onChange={(e) => setCidr(e.currentTarget.value)}
          disabled={!!jobId}
        />
        <NumberInput
          label="Duração (segundos)"
          value={duration}
          onChange={setDuration}
          min={5}
          max={60}
          disabled={!!jobId}
        />
        <Tooltip
          label="Bloqueado em tenant PCI"
          disabled={!hasPci}
          position="right"
          withArrow
        >
          <Switch
            label="Capturar payload completo"
            checked={fullPayload}
            onChange={(e) => setFullPayload(e.currentTarget.checked)}
            disabled={hasPci || !!jobId}
            data-testid="capture-full-payload"
          />
        </Tooltip>

        {!jobId && (
          <Group>
            <Button
              onClick={startCapture}
              loading={starting}
              disabled={!instanceId || !!jobId || Number(duration) < 5 || Number(duration) > 60 || noInstances}
            >
              Iniciar captura
            </Button>
          </Group>
        )}

        {/* Bloco de status da captura */}
        {jobId && isRunning && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text size="sm">Capturando... (job #{jobId})</Text>
          </Group>
        )}

        {jobId && isReady && captureStatus?.download_url && (
          <Group>
            <Text size="sm" c="teal">Captura concluída.</Text>
            <Button
              size="xs"
              variant="light"
              color="teal"
              component="a"
              href={captureStatus.download_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Baixar .pcap
            </Button>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => { setJobId(null); setCaptureError(null) }}
            >
              Nova captura
            </Button>
          </Group>
        )}

        {jobId && isFailed && (
          <Alert color="red" title="Captura falhou">
            {captureStatus?.error ?? 'Erro desconhecido na captura.'}
            <Box mt="xs">
              <Button size="xs" variant="subtle" color="red" onClick={() => { setJobId(null); setCaptureError(null) }}>
                Tentar novamente
              </Button>
            </Box>
          </Alert>
        )}

        {captureError && (
          <Alert color="red" title="Erro">
            {captureError}
          </Alert>
        )}
      </Stack>
    </Box>
  )
}
